/**
 * Kismet Quiz V2 — Resolver (shared by the wizard and the Reveal)
 *
 * Single source of truth for "what does the quiz recommend": route, recipe,
 * topper, bag size and delivery cadence, computed from the answers alone.
 * The Reveal (quiz-v2-results.js) matches these keys to catalog products;
 * the wizard (quiz-v2.js) attaches them to the Klaviyo `Completed Quiz`
 * event at the gate (Retencity, 2026-09-22) so flows can personalize without
 * waiting for the results page.
 *
 * Tables encode docs/quiz-v2-resolver-spec.md as reviewed by the client on
 * 2026-07-29. Corrections are data edits here and nowhere else.
 *
 * Allergens (2026-09-17): what a product contains is its `custom.allergens`
 * metafield (chicken / beef / fish, filled by Kismet). The Reveal passes a
 * `catalog` that reads it; the wizard has no catalog and uses the allergen
 * tables below, which mirror the metafields as loaded.
 *
 * Requires quiz.calculator.js (window.QuizCalculator) for calories/bag/cadence.
 */

(function () {
 'use strict';

 var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

 // ── Resolver tables ──────────────────────────────────────────────────────
 var RECIPES = {
  chicken: { match: ['chicken', 'barley'], label: 'Chicken & Barley' },
  salmon: { match: ['salmon', 'brown rice'], label: 'Salmon & Brown Rice' }
 };

 // Which recipes an allergen rules out when no catalog data is at hand (P6,
 // client-reviewed): beef blocks the salmon recipe because its freeze-dried
 // nugs contain beef.
 var ALLERGEN_BLOCKS = {
  chicken: ['chicken'],
  fish: ['salmon'],
  beef: ['salmon'],
  none: []
 };

 // Route → copy + add-ons (P10). `toppers` is a PREFERENCE CHAIN of Shakers
 // keywords: the route leads with its ideal shaker and substitutes with the
 // next allergy-safe one; empty chain = no topper. Ben (2026-07-31): "allergy
 // should take precedence over ideal recs" — lead with the current shaker,
 // swap only for allergy reasons, none if everything is blocked.
 // calloutTitle/calloutChip: benefit-led callout copy (client 2026-08-05 —
 // "the main message should be the problem the user wants to solve, not the
 // dog's personality"). {name} is replaced at render.
 var ROUTES = {
  puppy: {
   label: 'Puppy plan',
   headline: '{name}’s puppy plan food, topper, and treats.',
   calloutTitle: 'Best nutrition for {name}’s healthy growth',
   calloutChip: 'Built for Growing Puppies',
   toppers: [],
   treats: false, // pending Nutrition: are Squares puppy-safe?
   preferred: null
  },
  senior: {
   label: 'Senior mobility',
   headline: '{name}’s complete plan food, topper, and treats.',
   calloutTitle: 'Best nutrition for {name}’s joints & mobility',
   calloutChip: 'Senior Mobility Support',
   toppers: ['hip', 'skin'],
   treats: true,
   preferred: null
  },
  gut: {
   label: 'Gut health',
   headline: '{name}’s complete plan food, topper, and treats.',
   calloutTitle: 'Best nutrition for {name}’s sensitive stomach',
   calloutChip: 'Digestive Health Support',
   toppers: ['multivitamin', 'skin'],
   treats: false,
   preferred: 'salmon'
  },
  skin_coat: {
   label: 'Skin & coat',
   headline: '{name}’s complete plan food, topper, and treats.',
   calloutTitle: 'Best nutrition for {name}’s skin & coat',
   calloutChip: 'Skin & Coat Support',
   toppers: ['skin', 'multivitamin'],
   treats: true,
   preferred: 'salmon'
  },
  overall: {
   label: 'Overall health',
   headline: '{name}’s complete plan food, topper, and treats.',
   calloutTitle: 'Best nutrition for {name}’s everyday health',
   calloutChip: 'Complete Daily Nutrition',
   toppers: ['multivitamin', 'skin'],
   treats: true,
   preferred: null
  }
 };

 // Portion bands shown as the "10–25 lb portioned" chip (P12, display only)
 var WEIGHT_BANDS = [
  { max: 9, label: '1–9 lb' },
  { max: 25, label: '10–25 lb' },
  { max: 50, label: '26–50 lb' },
  { max: 100, label: '51–100 lb' },
  { max: Infinity, label: '100+ lb' }
 ];

 // ── Helpers ──────────────────────────────────────────────────────────────
 function ageMonths(dog) {
  var y = parseInt(dog.birthdayYear, 10);
  var m = MONTHS.indexOf(dog.birthdayMonth);
  var d = parseInt(dog.birthdayDay, 10);
  if (!y || m < 0 || !d) return null;
  var now = new Date();
  var months = (now.getFullYear() - y) * 12 + (now.getMonth() - m);
  if (now.getDate() < d) months -= 1;
  return Math.max(0, months);
 }

 function realAllergies(dog) {
  return (dog.allergies || []).filter(function (a) { return a !== 'none'; });
 }

 function topGoal(goals) {
  var order = ['gut', 'hip_joint', 'senior_care', 'skin_coat', 'energy', 'overall'];
  for (var i = 0; i < order.length; i++) {
   if ((goals || []).indexOf(order[i]) > -1) return order[i];
  }
  return '';
 }

 // ── Resolver ─────────────────────────────────────────────────────────────
 function resolveRoute(dog) {
  var months = ageMonths(dog);
  var goal = topGoal(dog.healthGoals);
  if ((months !== null && months < 12) || dog.motivation === 'new_puppy') return 'puppy';
  if ((months !== null && months >= 96) || goal === 'senior_care' || goal === 'hip_joint') return 'senior';
  if (goal === 'gut' || dog.motivation === 'gut') return 'gut';
  if (goal === 'skin_coat') return 'skin_coat';
  return 'overall';
 }

 function hasAny(list, allergies) {
  return allergies.some(function (a) { return list.indexOf(a) > -1; });
 }

 // `catalog` (optional): { recipeAllergens(key), topperAllergens(keyword) },
 // each returning the product's custom.allergens or null when there is no data.
 function recipeBlocked(recipeKey, allergies, catalog) {
  var list = catalog && catalog.recipeAllergens ? catalog.recipeAllergens(recipeKey) : null;
  if (list) return hasAny(list, allergies);
  return allergies.some(function (a) {
   return (ALLERGEN_BLOCKS[a] || []).indexOf(recipeKey) > -1;
  });
 }

 // Returns 'chicken' | 'salmon' | null (null = no safe recipe → AskAVet)
 function resolveRecipe(dog, preferred, catalog) {
  var allergies = realAllergies(dog);
  var order = preferred === 'salmon' ? ['salmon', 'chicken'] : ['chicken', 'salmon'];
  for (var i = 0; i < order.length; i++) {
   if (!recipeBlocked(order[i], allergies, catalog)) return order[i];
  }
  return null;
 }

 // Shaker base proteins (ingredient lists from the product pages, 2026-07-29):
 // Multivitamin & Hip & Joint are chicken recipes; Skin & Coat is a beef recipe.
 // All three contain salmon oil — per the client's own gut rule (Skin & Coat
 // stays available to fish-allergic dogs) the oil does NOT count as a fish
 // allergen; only the base protein blocks. Pending client confirmation.
 var TOPPER_ALLERGENS = {
  multivitamin: ['chicken'],
  hip: ['chicken'],
  skin: ['beef']
 };

 function topperBlocked(keyword, allergies, catalog) {
  var list = catalog && catalog.topperAllergens ? catalog.topperAllergens(keyword) : null;
  return hasAny(list || TOPPER_ALLERGENS[keyword] || [], allergies);
 }

 // A shaker containing one of the dog's allergens is never recommended:
 // walk the route's preference chain and pick the first safe one (Ben
 // 2026-07-31: allergy takes precedence; substitute, else none).
 function resolveTopper(route, dog, catalog) {
  var allergies = realAllergies(dog);
  var chain = route.toppers || [];
  for (var i = 0; i < chain.length; i++) {
   if (!topperBlocked(chain[i], allergies, catalog)) return chain[i];
  }
  return null;
 }

 function weightBand(lbs) {
  for (var i = 0; i < WEIGHT_BANDS.length; i++) {
   if (lbs <= WEIGHT_BANDS[i].max) return WEIGHT_BANDS[i].label;
  }
  return '';
 }

 // The legacy calculator's input shape (V2 stores the calculator's exact
 // label values for size/activity).
 function toCalculatorDog(dog) {
  return {
   name: dog.name,
   breed: dog.breed,
   birthdayMonth: dog.birthdayMonth,
   birthdayDay: parseInt(dog.birthdayDay, 10) || 1,
   birthdayYear: parseInt(dog.birthdayYear, 10) || new Date().getFullYear() - 3,
   weight: parseFloat(dog.weight) || 0,
   allergies: realAllergies(dog).length > 0,
   gender: dog.sex,
   spayedOrNeutered: dog.spayNeuter !== 'no',
   size: dog.bodyCondition,
   activityLevel: dog.activity
  };
 }

 /**
  * Full recommendation for one dog. `catalog` is optional (see recipeBlocked);
  * without it the result depends on the answers alone.
  * recipe === null means no safe recipe (Ask-a-Vet ending).
  */
 function resolve(dog, catalog) {
  var calc = window.QuizCalculator;
  var calories = calc ? calc.calculateRecommendations(toCalculatorDog(dog)) : 0;
  var bagWeight = calc ? calc.calculateBagWeight(calories) : '9';
  var cadence = calc ? calc.calculateDeliveryCadence(calories, bagWeight) : 4;
  var routeKey = resolveRoute(dog);
  var route = ROUTES[routeKey];
  var recipeKey = resolveRecipe(dog, route.preferred, catalog);
  return {
   routeKey: routeKey,
   route: route,
   recipeKey: recipeKey,
   recipeLabel: recipeKey ? RECIPES[recipeKey].label : '',
   topper: recipeKey ? resolveTopper(route, dog, catalog) : null,
   treats: !!(recipeKey && route.treats),
   calories: calories,
   bagWeight: bagWeight,
   cadence: cadence,
   band: weightBand(parseFloat(dog.weight) || 0)
  };
 }

 window.QuizResolver = {
  MONTHS: MONTHS,
  RECIPES: RECIPES,
  ALLERGEN_BLOCKS: ALLERGEN_BLOCKS,
  ROUTES: ROUTES,
  WEIGHT_BANDS: WEIGHT_BANDS,
  ageMonths: ageMonths,
  realAllergies: realAllergies,
  topGoal: topGoal,
  resolveRoute: resolveRoute,
  recipeBlocked: recipeBlocked,
  resolveRecipe: resolveRecipe,
  resolveTopper: resolveTopper,
  weightBand: weightBand,
  toCalculatorDog: toCalculatorDog,
  resolve: resolve
 };
})();
